/**
 * Surveys (v1) — the ONE source of truth for a survey's questions, answers and
 * results: the builder, the public fill form, the staff "Add response" form,
 * both API write routes and the Results tab all use this file (docs/SURVEYS.md).
 *
 *   surveyQuestionSchema       one question (discriminated union on `type`)
 *   surveyQuestionsSchema      the `surveys.questions` array (1–50, unique ids)
 *   sanitizeSurveyText         the one text scrubber (org-authored text AND text answers)
 *   buildSurveyAnswersSchema   the answers object for a given question list (server-authoritative)
 *   summarizeSurvey            pure results over loaded responses
 *   create / update / status / staff-response / public-submit request bodies
 *
 * SECURITY POSTURE (the public form is the first unauthenticated write
 * surface): stored text is PLAIN TEXT ONLY — no markup is interpreted
 * anywhere; renderers use React text nodes. `sanitizeSurveyText` removes the
 * characters that can spoof or smuggle (control chars, zero-width, bidi
 * overrides — "Trojan Source", tag characters, blank fillers), and the per-type caps here are the request
 * body-size control (the DB `pg_column_size` CHECKs are the backstop).
 * Deliberately NO question type carries a URL, image, file, HTML/markdown,
 * style, password, card number or hidden value.
 *
 * Dates: a `date` answer is a calendar day `YYYY-MM-DD`, a `time` answer a
 * wall-clock `HH:mm` — never an instant (docs/DATES.md).
 *
 * Every request object is `.strict()` (zod otherwise strips unknown keys
 * silently, so a body carrying `organization_id` would 200 with it dropped).
 */
import { z } from "zod";
import { calendarDateSchema, localTimeSchema } from "./dateWindow.js";
import { addDaysYmd, dbCalendarDayInZone, diffDaysYmd, formatLocalISODate } from "../format/date.js";
import { SURVEY_WEBSITE_LABEL_MAX, SURVEY_WEBSITE_URL_MAX, normalizeWebsiteUrl } from "./surveysUrl.js";
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
export const SURVEY_TITLE_MAX = 200;
export const SURVEY_DESCRIPTION_MAX = 5_000;
/** The respondent's thank-you screen text (the DB CHECK is the backstop). */
export const SURVEY_THANK_YOU_MAX = 500;
export const SURVEY_QUESTION_LABEL_MAX = 500;
export const SURVEY_QUESTION_HELP_MAX = 1_000;
export const SURVEY_OPTION_LABEL_MAX = 200;
export const SURVEY_SCALE_LABEL_MAX = 100;
export const SURVEY_MIN_QUESTIONS = 1;
export const SURVEY_MAX_QUESTIONS = 50;
export const SURVEY_MIN_OPTIONS = 2;
export const SURVEY_MAX_OPTIONS = 50;
export const SURVEY_MAX_GRID_ROWS = 20;
export const SURVEY_MAX_GRID_COLUMNS = 10;
export const SURVEY_SHORT_TEXT_MAX = 500;
export const SURVEY_LONG_TEXT_MAX = 5_000;
export const SURVEY_EMAIL_MAX = 320;
export const SURVEY_PHONE_MAX = 50;
/** The honeypot field name on the public submit body. A human never fills it. */
export const SURVEY_HONEYPOT_FIELD = "website";
/**
 * A raw string longer than `cap × this` is refused before it is scrubbed —
 * scrubbing can only shorten a string, so a value that far over the cap
 * cannot pass and is not worth the work.
 */
const RAW_SLACK = 4;
// ---------------------------------------------------------------------------
// sanitizeSurveyText — the ONE scrubber
// ---------------------------------------------------------------------------
/** Lone UTF-16 surrogates (Postgres `jsonb` refuses them; they are never text). */
const LONE_SURROGATES = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;
/**
 * Invisible format characters — every Unicode `Cf` (format) code point, which
 * covers the bidi controls (embeddings / overrides U+202A–202E, isolates
 * U+2066–2069, LRM / RLM / ALM — the "Trojan Source" set), ZWSP U+200B, the
 * word joiner and invisible operators U+2060–2064, BOM U+FEFF, the Mongolian
 * vowel separator U+180E, soft hyphen U+00AD ("pay\u00ADpal" renders as
 * "paypal"), interlinear annotation U+FFF9–FFFB and the TAG block
 * U+E0000–E007F (invisible text smuggling; the England / Scotland / Wales
 * flags degrade to a plain black flag) — plus the non-`Cf` invisibles: the
 * combining grapheme joiner U+034F and the visually blank Hangul fillers
 * U+115F, U+1160, U+3164, U+FFA0 (so a label cannot be all-filler).
 *
 * KEPT (excluded by the lookahead): ZWNJ U+200C and ZWJ U+200D are
 * orthographic (Persian / Urdu / Indic words) and join emoji sequences (👨‍👩‍👧);
 * the Arabic / Syriac / Kaithi prepended concatenation marks (U+0600–0605,
 * U+06DD, U+070F, U+08E2, U+110BD, U+110CD) are `Cf` but render visibly.
 */
const INVISIBLE_FORMAT = 
// eslint-disable-next-line no-misleading-character-class -- a set of single invisible code points (ZWJ/ZWNJ listed on purpose in the lookahead), each matched on its own under /u
/(?![\u200C\u200D\u0600-\u0605\u06DD\u070F\u08E2\u{110BD}\u{110CD}])[\p{Cf}\u034F\u115F\u1160\u3164\uFFA0]/gu;
/** Line breaks in all their spellings, incl. U+2028 / U+2029. */
const LINE_BREAKS = /\r\n|[\r\n\u2028\u2029\u0085]/g;
/** C0 (U+0000–001F), DEL and C1 (U+0080–009F). `\n` is removed from the class by the caller. */
// eslint-disable-next-line no-control-regex -- stripping control characters is the point
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g;
/**
 * Scrub one piece of survey text, org-authored or respondent-entered:
 *
 *   1. NFC-normalise (one spelling per visible string);
 *   2. drop lone surrogates;
 *   3. line breaks → `\n` (multiline) or a space (single line); a tab → a space;
 *   4. strip every other C0 / C1 control char and every invisible format char
 *      (zero-width, bidi, tags, fillers — `INVISIBLE_FORMAT`; ZWNJ / ZWJ kept);
 *   5. trim.
 *
 * It does NOT escape or strip markup: `<script>` stays the literal text
 * `<script>`, because nothing anywhere interprets survey text as markup (React
 * text nodes only). Escaping here would corrupt legitimate text ("a < b") and
 * double-escape on render. Length caps are the schema's job (after scrubbing).
 */
export function sanitizeSurveyText(value, options = {}) {
    const lineBreak = options.multiline ? "\n" : " ";
    return value
        .normalize("NFC")
        .replace(LONE_SURROGATES, "")
        .replace(LINE_BREAKS, lineBreak)
        .replace(/\t/g, " ")
        .replace(CONTROL_CHARS, (ch) => (ch === "\n" && options.multiline ? ch : ""))
        .replace(INVISIBLE_FORMAT, "")
        .trim();
}
/**
 * One VISIBLE character: anything outside the format / non-spacing-mark /
 * enclosing-mark / separator categories, the controls, and the visually blank
 * code points that are letters or symbols by category (U+2800 braille blank,
 * the Hangul fillers U+115F / U+1160 / U+3164 / U+FFA0) — plus the variation
 * selectors U+FE00–FE0F and U+E0100–E01EF spelled out (they are `Mn`).
 *
 * The scrubber KEEPS ZWNJ / ZWJ (orthographic, emoji sequences), so "is this
 * blank?" cannot be `clean === ""`: a string of only ZWJ, VS16, combining
 * marks or U+2800 survives scrubbing yet renders as nothing. Every non-blank
 * test (`surveyText`'s required check, `optionalSurveyText`'s null-out,
 * `isUnanswered`) asks this instead. The stored text is never altered by it.
 */
const VISIBLE_CHAR = 
// eslint-disable-next-line no-misleading-character-class -- a negated set of single code points (combining marks, variation selectors) that render as nothing; matched one at a time under /u
/[^\p{Cf}\p{Cc}\p{Mn}\p{Me}\p{Z}\s\u2800\u115F\u1160\u3164\uFFA0\uFE00-\uFE0F\u{E0100}-\u{E01EF}]/u;
/** Does scrubbed text contain at least one visible character? */
export function hasVisibleSurveyText(value) {
    return VISIBLE_CHAR.test(value);
}
/**
 * A scrubbed string schema: refuse absurd raw input, scrub, then enforce
 * `max` on the CLEAN text (what is stored is what is measured). `min > 0`
 * means "required": the clean text must hold a visible character
 * (`hasVisibleSurveyText`) — a label of only ZWJ / combining marks is blank.
 */
export function surveyText(max, { min = 0, multiline = false } = {}) {
    const clean = z.string().max(max);
    return z
        .string()
        .max(max * RAW_SLACK)
        .transform((value) => sanitizeSurveyText(value, { multiline }))
        .pipe(min > 0 ? clean.min(min, "Required").refine(hasVisibleSurveyText, "Required") : clean);
}
/** Optional nullable scrubbed text; `''` / whitespace / a visibly blank string → `null`. */
export function optionalSurveyText(max, multiline = false) {
    return z
        .string()
        .max(max * RAW_SLACK)
        .transform((value) => sanitizeSurveyText(value, { multiline }))
        .pipe(z.string().max(max))
        .transform((value) => (hasVisibleSurveyText(value) ? value : null))
        .nullable()
        .optional();
}
// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------
export const SURVEY_QUESTION_TYPES = [
    "short_text",
    "long_text",
    "single_choice",
    "dropdown",
    "multi_choice",
    "scale",
    "rating",
    "choice_grid",
    "checkbox_grid",
    "number",
    "date",
    "time",
    "email",
    "phone",
    "section",
];
const uuid = z.string().uuid();
/**
 * Volunteer-signup intake only (docs/VOLUNTEER_SIGNUP.md "Changing questions
 * after answers"): an answered question, option, grid row or column is
 * RETIRED instead of deleted — hidden from new answers (`activeIntakeQuestions`,
 * volunteerSignup.ts), kept so existing answers still resolve their labels.
 * Surveys never send it (their questions freeze once open).
 */
const retiredSchema = z.boolean().optional();
/** `{id, label}` — a choice option, a grid row or a grid column. */
export const surveyOptionSchema = z
    .object({
    id: uuid,
    label: surveyText(SURVEY_OPTION_LABEL_MAX, { min: 1 }),
    retired: retiredSchema,
})
    .strict();
const optionsSchema = z.array(surveyOptionSchema).min(SURVEY_MIN_OPTIONS).max(SURVEY_MAX_OPTIONS);
const gridRowsSchema = z.array(surveyOptionSchema).min(1).max(SURVEY_MAX_GRID_ROWS);
const gridColumnsSchema = z.array(surveyOptionSchema).min(2).max(SURVEY_MAX_GRID_COLUMNS);
const common = {
    id: uuid,
    label: surveyText(SURVEY_QUESTION_LABEL_MAX, { min: 1 }),
    help: optionalSurveyText(SURVEY_QUESTION_HELP_MAX, true),
    required: z.boolean().default(false),
    /**
     * Volunteer-signup intake only (docs/VOLUNTEER_SIGNUP.md): the answer is
     * Art. 9 / sensitive and is stored apart, visible to role leads and org
     * admins. Surveys never send it (the builder drops it); a sensitive
     * question is never required (`intakeQuestionsSchema`). Sections take no
     * answer and carry no flag.
     */
    sensitive: z.boolean().optional(),
    /** Volunteer-signup intake only: see `retiredSchema`. */
    retired: retiredSchema,
};
const shortTextQuestion = z.object({ ...common, type: z.literal("short_text") }).strict();
const longTextQuestion = z.object({ ...common, type: z.literal("long_text") }).strict();
const singleChoiceQuestion = z
    .object({ ...common, type: z.literal("single_choice"), options: optionsSchema })
    .strict();
const dropdownQuestion = z
    .object({ ...common, type: z.literal("dropdown"), options: optionsSchema })
    .strict();
const multiChoiceQuestion = z
    .object({
    ...common,
    type: z.literal("multi_choice"),
    options: optionsSchema,
    min_selections: z.number().int().min(1).max(SURVEY_MAX_OPTIONS).nullable().optional(),
    max_selections: z.number().int().min(1).max(SURVEY_MAX_OPTIONS).nullable().optional(),
})
    .strict();
const scaleQuestion = z
    .object({
    ...common,
    type: z.literal("scale"),
    min: z.union([z.literal(0), z.literal(1)]),
    max: z.number().int().min(2).max(10),
    min_label: optionalSurveyText(SURVEY_SCALE_LABEL_MAX),
    max_label: optionalSurveyText(SURVEY_SCALE_LABEL_MAX),
})
    .strict();
const ratingQuestion = z
    .object({ ...common, type: z.literal("rating"), max: z.number().int().min(3).max(10) })
    .strict();
const choiceGridQuestion = z
    .object({
    ...common,
    type: z.literal("choice_grid"),
    rows: gridRowsSchema,
    columns: gridColumnsSchema,
})
    .strict();
const checkboxGridQuestion = z
    .object({
    ...common,
    type: z.literal("checkbox_grid"),
    rows: gridRowsSchema,
    columns: gridColumnsSchema,
})
    .strict();
const finite = z.number().finite();
const numberQuestion = z
    .object({
    ...common,
    type: z.literal("number"),
    min: finite.nullable().optional(),
    max: finite.nullable().optional(),
    integer: z.boolean().default(false),
})
    .strict();
const dateQuestion = z.object({ ...common, type: z.literal("date") }).strict();
const timeQuestion = z.object({ ...common, type: z.literal("time") }).strict();
const emailQuestion = z.object({ ...common, type: z.literal("email") }).strict();
const phoneQuestion = z.object({ ...common, type: z.literal("phone") }).strict();
/** A display-only heading + text. Takes no answer, so it can never be required. */
const sectionQuestion = z
    .object({
    id: uuid,
    type: z.literal("section"),
    label: common.label,
    help: common.help,
    required: z.literal(false).default(false),
    retired: common.retired,
})
    .strict();
function duplicateIdIssues(items, ctx, path, what) {
    const seen = new Set();
    items.forEach((item, index) => {
        if (seen.has(item.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `duplicate ${what} id`,
                path: [...path, index, "id"],
            });
        }
        seen.add(item.id);
    });
}
/**
 * One question. The union's members are plain strict objects (a
 * discriminated union cannot hold refined members), so the cross-field rules
 * live in the `superRefine`: unique option / row / column ids, `min ≤ max`
 * on multi-choice selections and on number bounds, selections ≤ options.
 */
export const surveyQuestionSchema = z
    .discriminatedUnion("type", [
    shortTextQuestion,
    longTextQuestion,
    singleChoiceQuestion,
    dropdownQuestion,
    multiChoiceQuestion,
    scaleQuestion,
    ratingQuestion,
    choiceGridQuestion,
    checkboxGridQuestion,
    numberQuestion,
    dateQuestion,
    timeQuestion,
    emailQuestion,
    phoneQuestion,
    sectionQuestion,
])
    .superRefine((q, ctx) => {
    if ("options" in q)
        duplicateIdIssues(q.options, ctx, ["options"], "option");
    if (q.type === "choice_grid" || q.type === "checkbox_grid") {
        duplicateIdIssues(q.rows, ctx, ["rows"], "row");
        duplicateIdIssues(q.columns, ctx, ["columns"], "column");
    }
    if (q.type === "multi_choice") {
        const { min_selections: lo, max_selections: hi, options } = q;
        if (lo != null && hi != null && lo > hi) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "min_selections must not exceed max_selections",
                path: ["min_selections"],
            });
        }
        for (const [key, value] of [
            ["min_selections", lo],
            ["max_selections", hi],
        ]) {
            if (value != null && value > options.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${key} must not exceed the number of options`,
                    path: [key],
                });
            }
        }
    }
    if (q.type === "number" && q.min != null && q.max != null && q.min > q.max) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "min must not exceed max",
            path: ["min"],
        });
    }
});
export function isAnswerableQuestion(q) {
    return q.type !== "section";
}
/**
 * The `surveys.questions` array: 1–50 items, unique ids, at least one
 * question that takes an answer (a survey of headings only collects nothing).
 */
export const surveyQuestionsSchema = z
    .array(surveyQuestionSchema)
    .min(SURVEY_MIN_QUESTIONS)
    .max(SURVEY_MAX_QUESTIONS)
    .superRefine((questions, ctx) => {
    duplicateIdIssues(questions, ctx, [], "question");
    if (questions.length > 0 && !questions.some(isAnswerableQuestion)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "a survey needs at least one question that takes an answer",
        });
    }
});
const PHONE_SHAPE = /^\+?[0-9 ().\-/]+$/;
const PHONE_MIN_DIGITS = 5;
const PHONE_MAX_DIGITS = 15; // E.164
function digitCount(value) {
    return value.replace(/\D/g, "").length;
}
/**
 * "Not answered": absent, `null`, a visibly blank string (`hasVisibleSurveyText`), an empty
 * array or an empty object. Normalised to `undefined` BEFORE validation, so an
 * optional question accepts every spelling of "skipped" and a required one
 * refuses them all with the same message.
 */
function isUnanswered(value, multiline) {
    if (value === undefined || value === null)
        return true;
    if (typeof value === "string") {
        return value.length <= SURVEY_LONG_TEXT_MAX * RAW_SLACK && !hasVisibleSurveyText(sanitizeSurveyText(value, { multiline }));
    }
    if (Array.isArray(value))
        return value.length === 0;
    if (typeof value === "object")
        return Object.keys(value).length === 0;
    return false;
}
function uniqueIds(ids) {
    return new Set(ids).size === ids.length;
}
/** The value schema of one answerable question (assumes "answered"). */
function answerValueSchema(q) {
    switch (q.type) {
        case "short_text":
            return surveyText(SURVEY_SHORT_TEXT_MAX);
        case "long_text":
            return surveyText(SURVEY_LONG_TEXT_MAX, { multiline: true });
        case "email":
            return surveyText(SURVEY_EMAIL_MAX).pipe(z.string().email());
        case "phone":
            return surveyText(SURVEY_PHONE_MAX).pipe(z
                .string()
                .regex(PHONE_SHAPE, "not a phone number")
                .refine((v) => {
                const n = digitCount(v);
                return n >= PHONE_MIN_DIGITS && n <= PHONE_MAX_DIGITS;
            }, "not a phone number"));
        case "date":
            return calendarDateSchema;
        case "time":
            return localTimeSchema;
        case "single_choice":
        case "dropdown": {
            const ids = new Set(q.options.map((o) => o.id));
            return z.string().refine((v) => ids.has(v), "unknown option");
        }
        case "multi_choice": {
            const ids = new Set(q.options.map((o) => o.id));
            const lo = q.min_selections ?? 1;
            const hi = q.max_selections ?? q.options.length;
            return z
                .array(z.string().refine((v) => ids.has(v), "unknown option"))
                .min(lo, `select at least ${lo}`)
                .max(hi, `select at most ${hi}`)
                .refine(uniqueIds, "an option may be selected once");
        }
        case "scale":
            return z.number().int().min(q.min).max(q.max);
        case "rating":
            return z.number().int().min(1).max(q.max);
        case "number": {
            let schema = z.number().finite();
            if (q.integer)
                schema = schema.int();
            if (q.min != null)
                schema = schema.min(q.min);
            if (q.max != null)
                schema = schema.max(q.max);
            return schema;
        }
        case "choice_grid":
        case "checkbox_grid": {
            const columnIds = new Set(q.columns.map((c) => c.id));
            const column = z.string().refine((v) => columnIds.has(v), "unknown column");
            const cell = q.type === "choice_grid"
                ? column
                : z.array(column).min(1).max(q.columns.length).refine(uniqueIds, "a column may be selected once");
            // Every row optional at the row level — a skipped row in any spelling
            // (`null`, `""`, `[]`) normalises to absent, exactly like a skipped
            // question; a REQUIRED grid needs every row (Google Forms' "require a
            // response in each row") — checked below.
            const shape = {};
            const optionalCell = z.preprocess((raw) => (isUnanswered(raw, false) ? undefined : raw), cell.optional());
            for (const row of q.rows)
                shape[row.id] = optionalCell;
            const rowIds = q.rows.map((r) => r.id);
            return z
                .object(shape)
                .strict()
                .superRefine((value, ctx) => {
                if (!q.required)
                    return;
                for (const rowId of rowIds) {
                    if (value[rowId] === undefined) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Required", path: [rowId] });
                    }
                }
            })
                .transform((value) => {
                const clean = {};
                for (const [k, v] of Object.entries(value))
                    if (v !== undefined)
                        clean[k] = v;
                // Every row skipped → the question itself is unanswered (absent),
                // never a stored `{}` that would count as an answer.
                return Object.keys(clean).length > 0 ? clean : undefined;
            });
        }
    }
}
/**
 * The refusal message (and `params.code`) of an all-blank response — every
 * answerable question unanswered after the "not answered" normalisation.
 * One rule for public and staff responses alike.
 */
export const SURVEY_EMPTY_RESPONSE = "empty_response";
/**
 * The answers schema for `questions` — SERVER-AUTHORITATIVE: the API builds it
 * from the STORED questions, never from anything the client sent; the web
 * uses the same builder as its form resolver (UX only).
 *
 * A strict object keyed by question id (a key that is not an answerable
 * question → 400): required questions must be answered, choices must name an
 * existing option, numbers must be in range, text is scrubbed and capped (the
 * caps are the body-size control). The output holds only answered keys, and
 * must hold at least one: an all-blank response is refused with
 * `SURVEY_EMPTY_RESPONSE` (an issue at the root path). The check runs only
 * once every answer is otherwise valid, so a missing required answer still
 * reports "Required" on its own key.
 */
export function buildSurveyAnswersSchema(questions) {
    const shape = {};
    for (const q of questions) {
        if (!isAnswerableQuestion(q))
            continue;
        const multiline = q.type === "long_text";
        const value = answerValueSchema(q);
        shape[q.id] = z.preprocess((raw) => (isUnanswered(raw, multiline) ? undefined : raw), q.required ? value : value.optional());
    }
    return z
        .object(shape)
        .strict()
        .transform((value) => {
        const clean = {};
        for (const [k, v] of Object.entries(value)) {
            if (v !== undefined)
                clean[k] = v;
        }
        return clean;
    })
        .superRefine((clean, ctx) => {
        if (Object.keys(clean).length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: SURVEY_EMPTY_RESPONSE,
                params: { code: SURVEY_EMPTY_RESPONSE },
            });
        }
    });
}
// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------
export const SURVEY_STATUSES = ["draft", "open", "closed"];
const titleSchema = surveyText(SURVEY_TITLE_MAX, { min: 1 });
const descriptionSchema = optionalSurveyText(SURVEY_DESCRIPTION_MAX, true);
/** Plain text shown after a submit instead of the default sentence; NULL → the default. */
const thankYouMessageSchema = optionalSurveyText(SURVEY_THANK_YOU_MAX, true);
/**
 * The website button's own text (single line, ≤ 60); NULL → the default
 * ("Visit {hostname}" / "Visit {org}" — `surveyWebsiteButton`).
 */
const websiteLabelSchema = optionalSurveyText(SURVEY_WEBSITE_LABEL_MAX);
/** Max length of the line above the title on the public page (the DB CHECK is the backstop). */
export const SURVEY_INTRO_LINE_MAX = 120;
/**
 * The line above the title on the public page (single line, ≤ 120); NULL →
 * the default computed at render time and never stored: "{Project} · {Org}"
 * when the survey has a project, else "A survey from {Org}".
 */
const introLineSchema = optionalSurveyText(SURVEY_INTRO_LINE_MAX);
/**
 * The public page layout: `all` (every question on one page — the default)
 * or `one_at_a_time` (a stepper with progress). Answers have the same shape
 * either way.
 */
export const SURVEY_DISPLAY_MODES = ["all", "one_at_a_time"];
export const displayModeSchema = z.enum(SURVEY_DISPLAY_MODES);
/** The issue message when a survey's website override does not normalise (the web maps it to an i18n key). */
export const SURVEY_INVALID_WEBSITE_URL = "invalid_website_url";
/**
 * The survey's "Visit {org}" button link override. Blank → `null` (fall back
 * to the org's website); otherwise it must pass `normalizeWebsiteUrl` and is
 * STORED normalised (absolute http(s), public-looking host) — anything else
 * is a 400 carrying `SURVEY_INVALID_WEBSITE_URL`. The DB CHECK (≤ 2048,
 * `^https?://`) is the backstop.
 */
export const surveyWebsiteUrlSchema = z
    .string()
    .max(SURVEY_WEBSITE_URL_MAX * 2)
    .transform((value, ctx) => {
    if (!value.trim())
        return null;
    const normalized = normalizeWebsiteUrl(value);
    if (!normalized || normalized.length > SURVEY_WEBSITE_URL_MAX) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: SURVEY_INVALID_WEBSITE_URL });
        return z.NEVER;
    }
    return normalized;
})
    .nullable()
    .optional();
/** The issue message when a survey question carries the intake-only `retired` key. */
export const SURVEY_RETIRED_NOT_ALLOWED = "retired is for volunteer intake questions only";
/** Does any question, option, grid row or grid column carry `retired` (any value)? */
function carriesRetired(questions) {
    return questions.some((q) => {
        if (q.retired !== undefined)
            return true;
        const items = [
            ...("options" in q ? q.options : []),
            ...("rows" in q ? q.rows : []),
            ...("columns" in q ? q.columns : []),
        ];
        return items.some((o) => o.retired !== undefined);
    });
}
/**
 * A survey's questions on create / update: `surveyQuestionsSchema` minus the
 * intake-only `retired` key (surveys freeze once open — nothing to retire).
 */
const surveyOnlyQuestionsSchema = surveyQuestionsSchema.refine((qs) => !carriesRetired(qs), {
    message: SURVEY_RETIRED_NOT_ALLOWED,
});
/**
 * POST `/orgs/:orgId/surveys`. Always created as a `draft`.
 * DELIBERATELY ABSENT: `organization_id` (from `:orgId`), `code` (trigger),
 * `status` (draft; the status route moves it), `public_token` (minted by the
 * API on insert; regenerate-link replaces it), `created_by` (`auth.uid()`),
 * `digest_notified_at` (the digest job owns it), `public_logo_path` (the
 * server copies the org's current logo into the public space on create).
 * `website_url` is the optional "Visit {org}" link override (NULL → the org's
 * website); `website_label` the button's optional own text (NULL → the
 * default wording). `intro_line` is the optional line above the title
 * (NULL → the default); `display_mode` the layout (absent → `all`, the
 * column default). `public_token` is minted by the API (10 base62 chars).
 */
export const createSurveySchema = z
    .object({
    title: titleSchema,
    description: descriptionSchema,
    thank_you_message: thankYouMessageSchema,
    website_url: surveyWebsiteUrlSchema,
    website_label: websiteLabelSchema,
    intro_line: introLineSchema,
    display_mode: displayModeSchema.optional(),
    project_id: uuid.nullable().optional(),
    questions: surveyOnlyQuestionsSchema,
})
    .strict();
/**
 * PATCH `/orgs/:orgId/surveys/:id` — PRESENCE-KEYED (a column moves only when
 * its key is present; `null` clears `description` / `thank_you_message` /
 * `website_url` / `website_label` / `intro_line` / `project_id`). `questions`
 * may change only while the survey is a draft — the DB freeze guard refuses
 * otherwise (→ 409 `survey_frozen`); `thank_you_message`, `website_url`,
 * `website_label`, `intro_line` and `display_mode` stay editable after
 * opening.
 */
export const updateSurveySchema = z
    .object({
    title: titleSchema.optional(),
    description: descriptionSchema,
    thank_you_message: thankYouMessageSchema,
    website_url: surveyWebsiteUrlSchema,
    website_label: websiteLabelSchema,
    intro_line: introLineSchema,
    display_mode: displayModeSchema.optional(),
    project_id: uuid.nullable().optional(),
    questions: surveyOnlyQuestionsSchema.optional(),
})
    .strict()
    .refine((value) => Object.values(value).some((v) => v !== undefined), {
    message: "At least one field must be provided",
});
/**
 * POST `/orgs/:orgId/surveys/:id/status`. Never back to `draft` (the DB guard
 * refuses it too); closed → open (reopen) is allowed.
 */
export const surveyStatusSchema = z.object({ status: z.enum(["open", "closed"]) }).strict();
/**
 * The raw answers envelope. The route validates `answers` against
 * `buildSurveyAnswersSchema(<stored questions>)` — this schema only bounds the
 * shape (an object of at most `SURVEY_MAX_QUESTIONS` keys).
 */
const rawAnswersSchema = z
    .record(z.string(), z.unknown())
    .refine((value) => Object.keys(value).length <= SURVEY_MAX_QUESTIONS, "too many answers");
/** POST `/orgs/:orgId/surveys/:id/responses` — a staff-keyed response (`source='staff'`). */
export const staffSurveyResponseSchema = z.object({ answers: rawAnswersSchema }).strict();
/**
 * POST `/api/v1/public/surveys/:token` — the anonymous submit. `website` is
 * the HONEYPOT (rendered off-screen, never by a human): when it is filled the
 * route answers a fake 200 and stores nothing (`isSurveyHoneypotFilled`).
 */
export const publicSurveySubmitSchema = z
    .object({
    answers: rawAnswersSchema,
    // Every spelling a bot may send (null, a long stuffed value) must reach the
    // honeypot's fake 200, not a distinguishable 400 — so nullable, and capped
    // only as generously as the largest real answer.
    [SURVEY_HONEYPOT_FIELD]: z.string().max(SURVEY_LONG_TEXT_MAX * RAW_SLACK).nullable().optional(),
})
    .strict();
/** Did a bot fill the honeypot? Whitespace-only counts as empty (autofill quirks). */
export function isSurveyHoneypotFilled(body) {
    const value = body[SURVEY_HONEYPOT_FIELD];
    return typeof value === "string" && value.trim() !== "";
}
/** A response span longer than this many days is bucketed by month instead of by day. */
export const SURVEY_PER_DAY_MAX_DAYS = 366;
function zeroCounts(ids) {
    const counts = {};
    for (const id of ids)
        counts[id] = 0;
    return counts;
}
/** Count `key` and return true — only when it is a known key (else false, uncounted). */
function bump(counts, key) {
    // Only keys that exist — an answer naming an option the question no longer
    // has (the questions are frozen once open, so: hand-edited data) is ignored.
    if (!Object.prototype.hasOwnProperty.call(counts, key))
        return false;
    counts[key] += 1;
    return true;
}
function isAnswered(value) {
    if (value === undefined || value === null)
        return false;
    if (typeof value === "string")
        return value !== "";
    if (Array.isArray(value))
        return value.length > 0;
    if (typeof value === "object")
        return Object.keys(value).length > 0;
    return typeof value === "number" ? Number.isFinite(value) : true;
}
/**
 * Zero-filled buckets over the response days (calendar-day math only, no
 * instants — docs/DATES.md). By day when the span fits
 * `SURVEY_PER_DAY_MAX_DAYS`, else by month (keyed `YYYY-MM-01`).
 */
function perDaySeries(perDay) {
    const days = [...perDay.keys()].sort();
    if (days.length === 0)
        return { per_day: [], per_day_granularity: "day" };
    const first = days[0];
    const last = days[days.length - 1];
    const span = (diffDaysYmd(first, last) ?? 0) + 1;
    if (span <= SURVEY_PER_DAY_MAX_DAYS) {
        const series = [];
        for (let i = 0; i < span; i++) {
            const day = addDaysYmd(first, i);
            series.push({ day, count: perDay.get(day) ?? 0 });
        }
        return { per_day: series, per_day_granularity: "day" };
    }
    const byMonth = new Map();
    for (const [day, count] of perDay) {
        const month = `${day.slice(0, 7)}-01`;
        byMonth.set(month, (byMonth.get(month) ?? 0) + count);
    }
    const series = [];
    let year = Number(first.slice(0, 4));
    let month = Number(first.slice(5, 7));
    const end = `${last.slice(0, 7)}-01`;
    for (;;) {
        const key = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`;
        series.push({ day: key, count: byMonth.get(key) ?? 0 });
        if (key >= end)
            break;
        month += 1;
        if (month > 12) {
            month = 1;
            year += 1;
        }
    }
    return { per_day: series, per_day_granularity: "month" };
}
function mean(sum, n) {
    return n === 0 ? null : sum / n;
}
/**
 * The Results tab, computed from the questions and the loaded responses.
 * Pure and total: malformed answers (wrong type, unknown option) are skipped,
 * never thrown on.
 */
export function summarizeSurvey(questions, responses, options = {}) {
    const completed = { total: responses.length, public: 0, staff: 0 };
    const perDay = new Map();
    for (const r of responses) {
        if (r.source === "public")
            completed.public += 1;
        else if (r.source === "staff")
            completed.staff += 1;
        const day = formatLocalISODate(dbCalendarDayInZone(r.submitted_at, options.timeZone ?? null));
        if (day)
            perDay.set(day, (perDay.get(day) ?? 0) + 1);
    }
    const summaries = [];
    for (const q of questions) {
        if (!isAnswerableQuestion(q))
            continue;
        const values = responses.map((r) => r.answers?.[q.id]).filter(isAnswered);
        const base = { question_id: q.id, type: q.type };
        switch (q.type) {
            case "single_choice":
            case "dropdown":
            case "multi_choice": {
                const counts = zeroCounts(q.options.map((o) => o.id));
                let answered = 0;
                for (const v of values) {
                    const picked = q.type === "multi_choice" ? v : [v];
                    if (!Array.isArray(picked))
                        continue;
                    // Answered only when at least one pick is a known option (as the grid branch).
                    let any = false;
                    for (const id of new Set(picked))
                        if (typeof id === "string" && bump(counts, id))
                            any = true;
                    if (any)
                        answered += 1;
                }
                summaries.push({ ...base, kind: "choice", answered, option_counts: counts });
                break;
            }
            case "scale":
            case "rating": {
                const min = q.type === "scale" ? q.min : 1;
                const max = q.max;
                const distribution = zeroCounts(Array.from({ length: max - min + 1 }, (_, i) => String(min + i)));
                let sum = 0;
                let answered = 0;
                for (const v of values) {
                    if (typeof v !== "number" || !Number.isInteger(v) || v < min || v > max)
                        continue;
                    answered += 1;
                    sum += v;
                    distribution[String(v)] += 1;
                }
                summaries.push({ ...base, kind: "scale", answered, min, max, distribution, average: mean(sum, answered) });
                break;
            }
            case "number": {
                let sum = 0;
                let answered = 0;
                let lo = null;
                let hi = null;
                for (const v of values) {
                    if (typeof v !== "number" || !Number.isFinite(v))
                        continue;
                    answered += 1;
                    sum += v;
                    lo = lo === null ? v : Math.min(lo, v);
                    hi = hi === null ? v : Math.max(hi, v);
                }
                summaries.push({ ...base, kind: "number", answered, min: lo, max: hi, average: mean(sum, answered) });
                break;
            }
            case "choice_grid":
            case "checkbox_grid": {
                const columnIds = q.columns.map((c) => c.id);
                const cells = {};
                for (const row of q.rows)
                    cells[row.id] = zeroCounts(columnIds);
                let answered = 0;
                for (const v of values) {
                    if (typeof v !== "object" || v === null || Array.isArray(v))
                        continue;
                    let any = false;
                    for (const [rowId, cell] of Object.entries(v)) {
                        const rowCounts = Object.prototype.hasOwnProperty.call(cells, rowId) ? cells[rowId] : undefined;
                        if (!rowCounts)
                            continue;
                        const picked = Array.isArray(cell) ? cell : [cell];
                        for (const col of new Set(picked)) {
                            if (typeof col === "string" && bump(rowCounts, col))
                                any = true;
                        }
                    }
                    if (any)
                        answered += 1;
                }
                summaries.push({ ...base, kind: "grid", answered, cell_counts: cells });
                break;
            }
            default: {
                const answered = values.filter((v) => typeof v === "string").length;
                summaries.push({ ...base, kind: "text", answered });
            }
        }
    }
    return {
        completed,
        ...perDaySeries(perDay),
        questions: summaries,
    };
}
//# sourceMappingURL=surveys.js.map